import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useSystemMessage } from '../../contexts/SystemMessageContext';
import { Button } from './Button';
import { MODAL_BACKDROP_OPACITY } from '~/constants';
import { playSound, SoundType } from '~/lib/audio';
import type { ChromeVariant } from '../../types/systemMessage';

const chromeStyles: Record<
  ChromeVariant,
  {
    border: string;
    glow: string;
    title: string;
    shadow: string;
    backdrop: string;
    sparkles: boolean;
  }
> = {
  reward: {
    border: 'border-yellow-500/50',
    glow: 'from-yellow-500/10',
    title: 'text-yellow-400',
    shadow: 'shadow-yellow-500/20',
    backdrop: 'from-yellow-500/10',
    sparkles: true,
  },
  help: {
    border: 'border-neon-blue-500/50',
    glow: 'from-neon-blue-500/10',
    title: 'text-neon-blue-300',
    shadow: 'shadow-neon-blue-500/20',
    backdrop: 'from-neon-blue-500/5',
    sparkles: false,
  },
  alert: {
    border: 'border-red-500/50',
    glow: 'from-red-500/10',
    title: 'text-red-400',
    shadow: 'shadow-red-500/20',
    backdrop: 'from-red-500/5',
    sparkles: false,
  },
};

export function SystemMessageModal() {
  const { currentMessage, dismissMessage } =
    useSystemMessage();
  const [currentPageIndex, setCurrentPageIndex] =
    useState(0);

  useEffect(() => {
    if (currentMessage) {
      // Play sound based on chrome variant
      if (currentMessage.chrome === 'reward') {
        playSound(SoundType.RewardModal);
      } else if (currentMessage.chrome === 'help') {
        playSound(SoundType.InfoModal);
      }
      // 'alert' chrome doesn't play a sound
    }
  }, [currentMessage?.slug]); // Only trigger when message changes

  if (!currentMessage) return null;

  const chrome = chromeStyles[currentMessage.chrome];
  const currentPage =
    currentMessage.pages[currentPageIndex];
  const isLastPage =
    currentPageIndex === currentMessage.pages.length - 1;
  const isFirstPage = currentPageIndex === 0;

  const handleNext = () => {
    if (!isLastPage) setCurrentPageIndex(prev => prev + 1);
  };

  const handlePrevious = () => {
    if (!isFirstPage) setCurrentPageIndex(prev => prev - 1);
  };

  const handlePrimaryAction = () => {
    currentMessage.buttons.primary.action();
    if (isLastPage) {
      dismissMessage(true);
      setCurrentPageIndex(0);
    } else {
      handleNext();
    }
  };

  const handleSecondaryAction = () => {
    if (currentMessage.buttons.secondary) {
      currentMessage.buttons.secondary.action();
      dismissMessage(true);
      setCurrentPageIndex(0);
    }
  };

  const handleBackdropDismiss = () => {
    dismissMessage(false);
    setCurrentPageIndex(0);
  };

  return createPortal(
    <div
      data-testid="system-message-modal"
      className="fixed inset-0 z-50 flex items-center justify-center"
    >
      {/* Backdrop — always dismissible (silent, does not mark shown) */}
      <div
        className={`absolute inset-0 ${MODAL_BACKDROP_OPACITY} backdrop-blur-sm cursor-pointer`}
        onClick={handleBackdropDismiss}
      >
        <div
          className={`absolute inset-0 bg-gradient-radial ${chrome.backdrop} via-transparent to-transparent`}
        />
      </div>

      {/* Modal */}
      <div className="relative animate-reward-entrance">
        <div
          className={`relative bg-deep-blue-900 border-2 ${chrome.border} rounded-xl p-8 max-w-md mx-4 shadow-2xl ${chrome.shadow}`}
        >
          {/* Glow overlay */}
          <div
            className={`absolute inset-0 rounded-xl bg-gradient-to-b ${chrome.glow} to-transparent pointer-events-none`}
          />

          {/* Sparkle decorations (reward chrome only) */}
          {chrome.sparkles && (
            <>
              <div className="absolute -top-2 -left-2 w-4 h-4 animate-sparkle">
                <span className="text-yellow-400 text-lg">
                  ✦
                </span>
              </div>
              <div className="absolute -top-1 -right-3 w-4 h-4 animate-sparkle animation-delay-200">
                <span className="text-yellow-300 text-sm">
                  ✦
                </span>
              </div>
              <div className="absolute -bottom-2 left-1/4 w-4 h-4 animate-sparkle animation-delay-400">
                <span className="text-yellow-400">✦</span>
              </div>
              <div className="absolute -bottom-1 right-1/3 w-4 h-4 animate-sparkle animation-delay-300">
                <span className="text-yellow-300 text-xs">
                  ✦
                </span>
              </div>
            </>
          )}

          {/* Title */}
          <h2
            className={`font-pixel text-lg ${chrome.title} text-center mb-6 drop-shadow-glow`}
          >
            {currentPage.title}
          </h2>

          {/* Icon */}
          {currentPage.icon && (
            <div className="flex justify-center mb-6">
              <div className="animate-float">
                {currentPage.icon}
              </div>
            </div>
          )}

          {/* Content */}
          <div className="text-center text-neon-blue-200 text-sm mb-6">
            {currentPage.content}
          </div>

          {/* Page dots (multi-page only) */}
          {currentMessage.pages.length > 1 && (
            <div className="flex justify-center gap-2 mb-6">
              {currentMessage.pages.map((_, index) => (
                // eslint-disable-next-line custom-rules/prefer-button
                <button
                  key={index}
                  onClick={() => setCurrentPageIndex(index)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    index === currentPageIndex
                      ? 'bg-yellow-400 w-6'
                      : 'bg-yellow-400/30 hover:bg-yellow-400/50'
                  }`}
                  aria-label={`Go to page ${index + 1}`}
                />
              ))}
            </div>
          )}

          {/* Navigation arrows (multi-page only) */}
          {currentMessage.pages.length > 1 && (
            <div className="flex justify-between mb-4">
              {/* eslint-disable-next-line custom-rules/prefer-button */}
              <button
                onClick={handlePrevious}
                disabled={isFirstPage}
                className={`text-neon-blue-300 text-sm ${
                  isFirstPage
                    ? 'invisible'
                    : 'hover:text-neon-blue-100'
                }`}
              >
                ← Previous
              </button>
              {/* eslint-disable-next-line custom-rules/prefer-button */}
              <button
                onClick={handleNext}
                disabled={isLastPage}
                className={`text-neon-blue-300 text-sm ${
                  isLastPage
                    ? 'invisible'
                    : 'hover:text-neon-blue-100'
                }`}
              >
                Next →
              </button>
            </div>
          )}

          {/* CTAs */}
          <div
            data-testid="system-message-cta"
            className="space-y-3"
          >
            <Button
              onClick={handlePrimaryAction}
              className={`w-full font-pixel ${
                currentMessage.chrome === 'reward'
                  ? 'bg-yellow-600/30 hover:bg-yellow-600/50 border-yellow-500 text-yellow-300'
                  : ''
              }`}
            >
              {isLastPage
                ? currentMessage.buttons.primary.label
                : 'Next'}
            </Button>

            {currentMessage.buttons.secondary && (
              <Button
                onClick={handleSecondaryAction}
                className="w-full text-neon-blue-300"
              >
                {currentMessage.buttons.secondary.label}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
